import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { C, F, Card, Btn } from './tokens';
import { Eye, EyeOff } from 'lucide-react';

// Self-service Change Password card. Renders inside the Settings surface
// for any logged-in role. Calls supabase.auth.updateUser({password}) which
// operates against the current auth session — so the user changes their
// OWN password, regardless of any user-prop shimming in impersonation
// paths (App.js PortalShell rewrites user.role but does NOT switch the
// underlying Supabase session).
//
// isImpersonating: when true (master/sales is previewing as a client, or
// master is impersonating an advisor), the form is hidden and a note
// explains why. This prevents the impersonator from accidentally changing
// their own password while thinking they're in someone else's portal.
export default function ChangePasswordCard({ isImpersonating = false }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [accountEmail, setAccountEmail] = useState("");

  // Pull the email from the active Supabase session so the user can see
  // which account they're about to change the password for — important
  // when a master switches between accounts in the same browser.
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (cancelled) return;
      const email = data?.user?.email || "";
      if (email) setAccountEmail(email);
    });
    return () => { cancelled = true; };
  }, []);

  // Impersonation short-circuit. Render the same card shell with an
  // explanatory note instead of the form so the Settings page doesn't
  // visually shift between roles.
  if (isImpersonating) {
    return (
      <Card style={{padding:"16px 18px"}}>
        <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:600,marginBottom:6}}>Change Password</div>
        <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,lineHeight:1.55}}>
          You're working inside another user's portal. Return to your own account to change your password.
        </div>
      </Card>
    );
  }

  // Validation. We only show field-level errors after the user has typed
  // something in the relevant field, to avoid yelling on first paint.
  const lengthOk = newPassword.length >= 8;
  const matchOk = newPassword === confirm;
  const newTouched = newPassword.length > 0;
  const confirmTouched = confirm.length > 0;
  const lengthError = newTouched && !lengthOk;
  const matchError = newTouched && confirmTouched && !matchOk;
  const canSave = lengthOk && matchOk && !saving;

  // Both fields clear any stale success/error banners on edit, so the
  // feedback always reflects the latest save attempt.
  function changeNewPassword(v) {
    setNewPassword(v);
    if (success) setSuccess(false);
    if (error) setError("");
  }
  function changeConfirm(v) {
    setConfirm(v);
    if (success) setSuccess(false);
    if (error) setError("");
  }

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setError("");
    setSuccess(false);
    try {
      const { error: updateErr } = await supabase.auth.updateUser({ password: newPassword });
      if (updateErr) throw updateErr;
      setNewPassword("");
      setConfirm("");
      setShowPassword(false);
      setSuccess(true);
    } catch (e) {
      setError(e?.message || "Failed to update password. Please try again.");
    }
    setSaving(false);
  }

  // Shared input + eye-toggle render function. Not a React component — a
  // plain function returning JSX — so React doesn't remount on each render.
  // A single showPassword state drives both eye buttons; clicking either
  // toggles both fields.
  function renderPasswordInput({ value, onChange, placeholder, autoComplete, onKeyDown }) {
    return (
      <div style={{position:"relative"}}>
        <input
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={e=>onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoComplete={autoComplete}
          style={{
            width:"100%",
            background:C.bgAlt,
            border:`1px solid ${C.border}`,
            borderRadius:8,
            padding:"11px 40px 11px 13px",
            color:C.charcoal,
            fontFamily:F.body,
            fontSize:13,
            boxSizing:"border-box",
          }}
        />
        <button
          type="button"
          onClick={()=>setShowPassword(s=>!s)}
          aria-label={showPassword?"Hide password":"Show password"}
          tabIndex={-1}
          style={{
            position:"absolute",
            right:8,
            top:"50%",
            transform:"translateY(-50%)",
            background:"transparent",
            border:"none",
            padding:6,
            cursor:"pointer",
            color:C.charLight,
            display:"flex",
            alignItems:"center",
            lineHeight:0,
          }}
        >
          {showPassword?<EyeOff size={16}/>:<Eye size={16}/>}
        </button>
      </div>
    );
  }

  return (
    <Card style={{padding:"16px 18px"}}>
      <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:600,marginBottom:4}}>Change Password</div>
      <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,marginBottom:accountEmail?2:14}}>Set a new password for your account.</div>
      {accountEmail && (
        <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginBottom:14}}>Account: {accountEmail}</div>
      )}

      <div style={{marginBottom:12}}>
        <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:6,fontWeight:600,letterSpacing:".07em",textTransform:"uppercase"}}>New password</label>
        {renderPasswordInput({
          value: newPassword,
          onChange: changeNewPassword,
          placeholder: "At least 8 characters",
          autoComplete: "new-password",
        })}
        {lengthError && (
          <div style={{fontFamily:F.body,fontSize:11,color:C.red,marginTop:5}}>Password must be at least 8 characters.</div>
        )}
      </div>

      <div style={{marginBottom:12}}>
        <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:6,fontWeight:600,letterSpacing:".07em",textTransform:"uppercase"}}>Confirm new password</label>
        {renderPasswordInput({
          value: confirm,
          onChange: changeConfirm,
          placeholder: "Re-enter your new password",
          autoComplete: "new-password",
          onKeyDown: e => { if (e.key === "Enter" && canSave) handleSave(); },
        })}
        {matchError && (
          <div style={{fontFamily:F.body,fontSize:11,color:C.red,marginTop:5}}>Passwords don't match.</div>
        )}
      </div>

      {error && (
        <div style={{background:C.redBg,border:`1px solid ${C.redBd}`,borderRadius:7,padding:"9px 13px",fontFamily:F.body,fontSize:12,color:C.red,marginBottom:12}}>
          {error}
        </div>
      )}
      {success && (
        <div style={{background:C.greenBg,border:`1px solid ${C.greenBd}`,borderRadius:7,padding:"9px 13px",fontFamily:F.body,fontSize:12,color:C.green,marginBottom:12}}>
          Password updated. Use it next time you sign in.
        </div>
      )}

      <Btn variant="primary" onClick={handleSave} loading={saving} disabled={!canSave}>
        Update Password
      </Btn>
    </Card>
  );
}
